import React, { useEffect, useRef, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { getSecureImageUrl } from '../../services/secureImageUrls';

/**
 * Gallery image that only resolves its presigned URL once it scrolls near the
 * viewport, requests the server-side thumbnail instead of the original, and
 * shares one batched URL request with its neighbours. Drop-in for the gallery
 * images that previously used AuthenticatedImage with the full-size file.
 */
const LazyThumbnail = React.forwardRef(({ src, alt, sx, width = 480, onClick, onError, onLoad, rootMargin = '600px' }, ref) => {
  const hostRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const retriedRef = useRef(false);

  // Become "visible" once near the viewport; stay visible afterwards.
  useEffect(() => {
    const el = hostRef.current;
    if (!el || visible) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin });
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  useEffect(() => {
    if (!visible || !src) return undefined;
    let cancelled = false;
    setFailed(false);
    retriedRef.current = false;
    getSecureImageUrl(src, { variant: 'thumbnail', width })
      .then((resolved) => { if (!cancelled) setUrl(resolved); })
      .catch((err) => {
        if (cancelled) return;
        setFailed(true);
        if (onError) onError(err);
      });
    return () => { cancelled = true; };
    // onError is deliberately left out: callers pass inline functions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, src, width]);

  // A presigned URL can expire while a long gallery stays open; refresh once.
  const handleImgError = (e) => {
    if (!retriedRef.current && src) {
      retriedRef.current = true;
      getSecureImageUrl(src, { variant: 'thumbnail', width, force: true })
        .then(setUrl)
        .catch(() => setFailed(true));
      return;
    }
    setFailed(true);
    if (onError) onError(e);
  };

  if (!url) {
    return (
      <Box ref={hostRef} sx={{ position: 'relative', ...sx }} onClick={onClick}>
        <Skeleton variant="rectangular" animation={failed ? false : 'wave'} sx={{ width: '100%', height: '100%' }} />
      </Box>
    );
  }

  return (
    <Box ref={hostRef} sx={{ position: 'relative', ...sx }}>
      <Box
        component="img"
        ref={ref}
        src={url}
        alt={alt}
        loading="lazy"
        decoding="async"
        sx={{ width: '100%', height: '100%', objectFit: sx?.objectFit || 'cover', display: 'block', cursor: onClick ? 'pointer' : undefined }}
        onClick={onClick}
        onError={handleImgError}
        onLoad={onLoad}
      />
    </Box>
  );
});

export default React.memo(LazyThumbnail);
